import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { getMainDefinition } from "@apollo/client/utilities";
import { ConnectionMonitor, createConsumer } from "@rails/actioncable";
import NotificationView from "components/common/NotificationView";
import ActionCableLink from "components/gqlLinks/ActionCableLink.js";
import createErrorLink from "components/gqlLinks/errorLink.js";
import createGenericMutationErrorLink from "components/gqlLinks/genericMutationErrorLink.js";
import endpoints from "config/endpoints";
import mapValues from "lodash/mapValues";
import React, { useState } from "react";
import TimeZone from "utils/TimeZone";

ConnectionMonitor.staleThreshold = 30;
const cable = createConsumer(endpoints.cable);

const onDisconnect = (props) => {
  if (!props.willAttemptReconnect) {
    document.location.replace(endpoints.login);
  }
};

const actionCableLink = new ActionCableLink({ cable, onDisconnect });
const mergeOverriddenTimeZone = (_existing, incoming) => TimeZone.overrideToBrowserTime(incoming);

const TIME_FIELDS = {
  Company: ["createdAt", "updatedAt"],
  DowntimeBlock: ["startsAt", "endsAt"],
  ShiftInstance: ["scheduledStart", "scheduledEnd"],
  Site: ["lastSyncedAt", "createdAt", "updatedAt"],
  SyncResult: ["lastSyncedAt"],
  Unavailability: ["startsAt", "endsAt"],
  User: ["createdAt", "updatedAt"],
  WorkBlock: ["startsAt", "endsAt", "workTimeStartsAt", "workTimeEndsAt"],
  WorkOrder: ["due", "productionLastUpdatedAt"],
};

const typePolicies = mapValues(TIME_FIELDS, (fields) => ({
  fields: fields.reduce((acc, field) => {
    acc[field] = { merge: mergeOverriddenTimeZone };

    return acc;
  }, {}),
}));

typePolicies.ShiftInstance.keyFields = ["shiftId", "scheduledStart"];

const cache = new InMemoryCache({ typePolicies });

const errorLink = createErrorLink();

const httpLink = new HttpLink({ credentials: "include", uri: endpoints.graphql });

function isGraphqlSubscription({ query }) {
  const definition = getMainDefinition(query);

  return definition.kind === "OperationDefinition" && definition.operation === "subscription";
}

const splitLink = ApolloLink.split(isGraphqlSubscription, actionCableLink, httpLink);

const createClient = (setShowGenericError) =>
  new ApolloClient({
    cache,
    link: ApolloLink.from([errorLink, createGenericMutationErrorLink(setShowGenericError), splitLink]),
  });

function GqlProviderWrapper(props) {
  const [showGenericError, setShowGenericError] = useState(false);

  const notificationProps = {
    closeModal: () => setShowGenericError(false),
    isCloseable: true,
    isOpen: showGenericError,
    message: "A scheduling error occurred. Please try again.",
    title: null,
    type: "danger",
  };

  return (
    <ApolloProvider {...{ client: createClient(setShowGenericError) }} {...{ cache }}>
      <NotificationView {...notificationProps} />
      {props.children}
    </ApolloProvider>
  );
}

export default GqlProviderWrapper;
